// src/features/reviewServices/reviewServicesSlice.js
// Holds the creator's OWN "Review My Work" service catalog — same shape/need
// as itemsSlice.js (a creator's own listings), which this mirrors. Orders,
// chat, and admin data intentionally do NOT get a slice — they're per-page,
// frequently-refetched, and don't need cross-route sharing (matching the
// existing Events feature's precedent of local component state only).
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

const initialState = {
  services: [],
  currentService: null,
  loading: false,
  error: null,
};

export const fetchReviewServices = createAsyncThunk(
  'reviewServices/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await apiService.getMyReviewServices();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch review services');
    }
  }
);

export const fetchReviewServiceById = createAsyncThunk(
  'reviewServices/fetchById',
  async (serviceId, { rejectWithValue }) => {
    try {
      return await apiService.getReviewService(serviceId);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch review service');
    }
  }
);

export const createReviewServiceThunk = createAsyncThunk(
  'reviewServices/create',
  async (data, { rejectWithValue }) => {
    try {
      return await apiService.createReviewService(data);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create review service');
    }
  }
);

export const updateReviewServiceThunk = createAsyncThunk(
  'reviewServices/update',
  async ({ serviceId, updates }, { rejectWithValue }) => {
    try {
      return await apiService.updateReviewService(serviceId, updates);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update review service');
    }
  }
);

export const deleteReviewServiceThunk = createAsyncThunk(
  'reviewServices/delete',
  async (serviceId, { rejectWithValue }) => {
    try {
      await apiService.deleteReviewService(serviceId);
      return serviceId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete review service');
    }
  }
);

const reviewServicesSlice = createSlice({
  name: 'reviewServices',
  initialState,
  reducers: {
    setCurrentReviewService: (state, action) => {
      state.currentService = action.payload;
    },
    clearReviewServicesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviewServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload;
      })
      .addCase(fetchReviewServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchReviewServiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReviewServiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentService = action.payload;
      })
      .addCase(fetchReviewServiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createReviewServiceThunk.fulfilled, (state, action) => {
        state.services.unshift(action.payload);
        state.currentService = action.payload;
      })
      .addCase(createReviewServiceThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateReviewServiceThunk.fulfilled, (state, action) => {
        const index = state.services.findIndex((s) => s.service_id === action.payload.service_id);
        if (index !== -1) state.services[index] = { ...state.services[index], ...action.payload };
        if (state.currentService?.service_id === action.payload.service_id) {
          state.currentService = { ...state.currentService, ...action.payload };
        }
      })
      .addCase(updateReviewServiceThunk.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteReviewServiceThunk.fulfilled, (state, action) => {
        state.services = state.services.filter((s) => s.service_id !== action.payload);
        if (state.currentService?.service_id === action.payload) state.currentService = null;
      })
      .addCase(deleteReviewServiceThunk.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setCurrentReviewService, clearReviewServicesError } = reviewServicesSlice.actions;
export default reviewServicesSlice.reducer;
